<template>
  <div class="pb-table-container" @scroll="loadMore">
    <modal-component
      class="modal"
      :class="modalClass"
      :item="activeItem"
      :type="formType"
      :show="modal"
      @close="closeModal"
      v-if="!!$scopedSlots['form']"
    >
      <slot
        name="form"
        v-bind="{ item: activeItem, type: formType, actions, show: modal }"
        :onAdd="addItem"
        :onEdit="openModal"
        :onUpdate="updateItem"
        :onDelete="deleteItem"
        :onClose="closeModal"
        :onArchive="archiveItem"
        :onDetach="detachItem"
      />
    </modal-component>

    <table :class="{ loading: loading && !items }">
      <thead>
        <tr v-if="loading && !items">
          <th>
            <div class="pbt-container">
              <div class="pbt-progress pbt-progress-long"></div>
              <div class="pbt-progress pbt-progress-short"></div>
            </div>
          </th>
        </tr>
        <tr v-else-if="!isMobile">
          <th class="multi-select" v-if="!hideSelect">
            <div class="multi-select form-check" v-if="multiSelect">
              <input
                type="checkbox"
                class="form-check-input"
                v-model="selectAll"
                @change="selectAllChange"
              />
            </div>
          </th>
          <th
            v-for="(header, index) in rowHeads"
            :class="[
              {
                sortable: header.sortable,
                'sortable-active': sort.col === header.value,
              },
              header.headerClass,
            ]"
            @click="sortTable(header)"
            :key="index"
          >
            <p>
              {{ header.name }}
              <svg
                :class="{ 'sort-desc': !sort.asc }"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                role="img"
                aria-hidden="true"
                class="v-icon__svg"
              >
                <path
                  d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z"
                ></path>
              </svg>
            </p>
          </th>
          <th v-if="childRows"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="loading && (!items || (Array.isArray(items) && !items.length))"
        >
          <td :colspan="headers.length">Loading... Please wait</td>
        </tr>
        <tr
          v-else
          v-for="(item, index) in rows"
          :key="index"
          :class="[
            towDateReached(item),
            {
              selected: selected.includes(item.id),
              'child-row': item.childRow,
            },
          ]"
        >
          <td
            v-if="!item.childRow && !isMobile && !hideSelect"
            class="multi-select"
          >
            <div class="multi-select form-check">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="selected.includes(item)"
                @click.stop="(val) => selectItem(val.target.checked, item)"
              />
            </div>
          </td>
          <td
            v-if="childRows && item.childRow"
            v-show="item.checked"
            :colspan="childSpan + 1"
            class="childrow"
          >
            <slot
              name="childrow"
              v-bind="{ item }"
              :onDelete="deleteSubRowItem"
              :onArchive="archiveSubRowItem"
            />
          </td>
          <td
            v-else
            v-for="(header, index) in headers"
            :key="index"
            @click="viewItem(item, header)"
            :class="header.rowClass"
          >
            <div class="cell-container">
              <p v-if="isMobile" class="bolded">{{ header.name }}</p>
              <slot
                :name="header.value"
                v-if="typeof $scopedSlots[header.value] !== 'undefined'"
                :item="item"
              ></slot>
              <p class="data" v-else>
                {{ processValue(item[header.value], header) }}
              </p>
            </div>
          </td>
          <td v-if="childRows && !item.childRow" class="arrow-td">
            <div
              class="cell-container subchild-arrow"
              :class="{ rotate: !item.checked }"
            >
              <svg class="row-arrow" @click="toggleChild(item)">
                <use xlink:href="#table-arrow" />
              </svg>
            </div>
          </td>
        </tr>
        <tr v-if="loading && rows && rows.length > 0">
          <td :colspan="rows.length">Loading...</td>
        </tr>
      </tbody>
    </table>
    <no-data v-if="!loading && items && items.length === 0" />
    <div class="clear-search-results" v-if="reset && items && items.length > 0">
      <a class="btn" @click="$emit('onReset')" title="Reset Searched Items">
        Reset Search
      </a>
    </div>
  </div>
</template>

<script>
import ModalComponent from "@/components/helpers/ModalComponent";
import router from "@/router";
import { mapGetters } from "vuex";
import ROLE_TYPES from "@/roles/roles";

export default {
  components: {
    ModalComponent,
  },
  props: {
    headers: {
      type: Array,
      default: function () {
        return [];
      },
    },
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Object,
      default: () => {
        return {
          edit: false,
          delete: false,
        };
      },
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    hideSelect: {
      type: Boolean,
      default: false,
    },
    add: {
      type: Boolean,
      default: false,
    },
    childRows: {
      type: Boolean,
      default: false,
    },
    childRow: {
      type: String,
      default: "",
    },
    reset: {
      type: Number,
      default: null,
    },
    orderBy: {
      type: String,
      default: null,
    },
    orderRule: {
      type: String,
      default: null,
    },
    clickableDesktop: {
      type: Boolean,
      default: true,
    },
    clickableMobile: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: Object,
      default: () => ({}),
    },
    active: {
      type: Object,
      default: () => {},
    },
    activeTab: {
      type: String,
      default: "",
    },
    propagateClick: {
      type: Boolean,
      default: false,
    },
    modalClass: {
      type: String,
      default: "",
    },
    showEnforcementDateColors: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rowHeads: this.headers,
      sort: {
        col: null,
        asc: false,
      },
      modal: false,
      activeItem: {},
      formType: null,
      selected: [],
      selectAll: false,
      mobileSort: {},
    };
  },
  created() {
    this.setupRouter();
  },
  methods: {
    towDateReached(item) {
      if (
        this.userRole === ROLE_TYPES.TOW_DRIVER &&
        this.showEnforcementDateColors &&
        item.enforcement_date
      ) {
        const enforcementDate = new Date(item.enforcement_date);
        const today = new Date();
        if (today >= enforcementDate) {
          return "tow-allowed";
        }
        return "tow-not-allowed";
      }
      return "";
    },
    setupRouter() {
      router.beforeEach((to, from, next) => {
        if (!this.modal || to.params.ignoreMiddleware) {
          next();
        } else {
          this.modal = false;
          next(false);
        }
      });
    },
    selectAllChange() {
      if (this.selectAll) {
        this.selected = [...this.rows];
      } else {
        this.selected = [];
      }
      this.$emit("onSelect", this.selected);
    },
    toggleChild(item) {
      item.checked = !item.checked;
      this.items = [...this.items];
    },
    sortTable(col) {
      if (!col.sortable) return;

      this.sort.asc = !this.sort.asc;
      const key = col.value;
      const asc = this.sort.asc;
      this.sort.col = key;
      this.sort.name = col.name;
      this.sort.item = col;

      const arr = this.rows.sort((a, b) => {
        if (typeof a[key] === "number" && typeof b[key] === "number") {
          return asc ? a[key] - b[key] : b[key] - a[key];
        }
        const aVal = a[key] ? a[key].toString() : "";
        const bVal = b[key] ? b[key].toString() : "";
        return asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
      this.updateArray(arr);
    },
    selectItem(selected, item) {
      if (selected) {
        if (this.multiSelect) {
          this.selected.push(item);
        } else {
          this.selected = [item];
        }
      } else {
        if (this.selected.length === 1) {
          this.selected = [];
        } else {
          const index = this.selected.indexOf(item);
          this.selected.splice(index, 1);
        }
      }
      this.$emit("onSelect", this.selected);
    },
    viewItem(item, header) {
      if (header.preventClick || (!this.isMobile && !this.clickableDesktop)) {
        return;
      } else if (
        header.preventClick ||
        (this.isMobile && !this.clickableMobile)
      ) {
        return;
      }
      if (!this.propagateClick) {
        this.activeItem = Object.assign({}, item);
        this.formType = "view";
        this.modal = true;
      } else {
        this.$emit("onRowClick", item);
      }
    },
    editItem(item) {
      this.activeItem = Object.assign({}, item);
      this.formType = "edit";
      this.modal = true;
    },
    // Delete the item and emit to parent
    deleteItem(item) {
      this.$emit("onDelete", item);
    },
    detachItem(item) {
      this.$emit("onDetach", item);
    },
    addItem(item) {
      this.closeModal();
      this.$emit("onAdd", item);
    },
    updateItem(item) {
      this.$emit("onUpdate", item);
      this.closeModal();
    },
    archiveItem(item) {
      this.$emit("onArchive", item);
      this.closeModal();
    },
    deleteSubRowItem(item) {
      this.$emit("onDelete", item);
    },
    archiveSubRowItem(item) {
      this.$emit("onArchive", item);
      this.closeModal();
    },
    updateArray(arr) {
      this.$emit("updateArray", arr);
    },
    openModal() {
      this.formType = "edit";
    },
    closeModal() {
      this.activeItem = {};
      this.formType = null;
      this.modal = false;
      this.$emit("onClose");
    },
    loadMore({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (!this.isMobile) return;

      if (Math.round(scrollTop) + clientHeight >= scrollHeight) {
        this.$emit("scrollEnd");
      }
    },
    processValue(value, header) {
      let val = value;
      if (header.parser) {
        val = this.$options.filters[header.parser](value);
      }

      if (header.max && value.length > header.max + 2) {
        val = value.substring(0, header.max) + "..";
      }

      return val;
    },
  },
  computed: {
    ...mapGetters({
      userRole: "authStore/getUserRole",
    }),
    rows() {
      if (!this.items) return [];
      if (!this.childRows) {
        return this.items;
      } else {
        const items = [];
        this.items.forEach((item) => {
          items.push(item, { ...item, childRow: true });
        });
        return items;
      }
    },
    childSpan() {
      let colspan = this.headers.length + 1;
      if (this.multiSelect) colspan += 1;
      return colspan;
    },
    statusCircle: function () {
      const status = this.items.status;
      if (status === true) {
        return "Active";
      } else {
        return "Inactive";
      }
    },
    sortableRows() {
      return this.rowHeads.filter((header) => {
        return header.sortable;
      });
    },
    fieldRules() {
      return Object.keys(this.rules);
    },
  },
  watch: {
    items: {
      handler() {
        this.selected.forEach((row, index) => {
          if (!this.rows.includes(row)) {
            this.selected.splice(index, 1);
          }
        });
      },
      deep: true,
    },
    active: {
      handler() {
        if (this.active) {
          this.activeItem = Object.assign({}, this.active);
          this.formType = "edit";
          this.modal = true;
        }
      },
      immediate: true,
      deep: true,
    },
    activeTab: function () {
      this.selectAll = false;
      this.selected = [];
      this.closeModal();
    },
    headers: {
      handler: function () {
        this.rowHeads = [];

        for (const key in this.headers) {
          const field = this.headers[key];
          if (
            !this.fieldRules.includes(field.value) ||
            (this.fieldRules.includes(field.value) && this.rules[field.value])
          ) {
            this.rowHeads.push(field);
          }
        }
      },
      immediate: true,
      deep: true,
    },
    add: {
      handler() {
        if (this.add) {
          this.modal = true;
          this.formType = "add";
          this.activeItem = {};
        }
      },
      deep: true,
      immediate: true,
    },
    orderBy: function () {
      this.sortTable(this.orderBy);
    },
    orderRule: function () {
      if (this.orderRule === "asc") {
        this.sort.asc = true;
      } else {
        this.sort.asc = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/partials/PbTable.vue